import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Button from '@renderer/components/Button'
import BahelaButton from '@renderer/components/bahela/Button'
import { useSessionStateStore, flushSessionLogs } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'
import {
  clearCheckpoint,
  describePackages,
  isOfferable,
  msUntilExpiry,
  readCheckpoint,
  RESUME_PAID_SESSION_FLAG,
  type SessionCheckpoint
} from '@renderer/utils/sessionCheckpoint'
import { useFeatureFlag } from '@renderer/hooks/useFeatureFlag'

/**
 * Offers a paid session that the booth's own crash recovery interrupted back to the
 * guest in front of the booth (see utils/sessionCheckpoint), in the guest's words: most
 * booths run unattended, and after a crash the payer is nearly always the next person
 * there. What they paid, when, and what they bought is shown so the payer recognises
 * it. Resume keeps the payment and choices and goes where a freshly paid session goes.
 * "Not my session" lets someone else start fresh without a PIN (otherwise they would
 * be stuck behind the prompt). Shown, declined and expired offers are all logged on
 * the paid session; an expired one is the refund cue.
 *
 * `nextPage` is where this flow's own Payment page sends a settled guest. Omit it
 * for the view flow; custom flows (pages/bahela) route differently and pass theirs.
 *
 * `appearance` follows the flow's look. The view flow is styled by the project
 * theme (Button reads `config.theme`). Bahela pages ignore that theme (every Bahela
 * project still carries the default #007AFF), so they pass 'bahela' and get
 * Bahela's own modal language: methadone card, uriYellow border, Bahela fonts.
 */
const InterruptedPaidSessionPrompt: React.FC<{
  projectId: string | undefined
  nextPage?: string
  appearance?: 'theme' | 'bahela'
}> = ({ projectId, nextPage: flowNextPage, appearance = 'theme' }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [checkpoint, setCheckpoint] = useState<SessionCheckpoint | null>(null)
  const offeredRef = useRef<string | null>(null)
  const enabled = useFeatureFlag(RESUME_PAID_SESSION_FLAG)

  useEffect(() => {
    if (!enabled) return
    const cp = readCheckpoint()
    const activeSessionId = useSessionStateStore.getState().id
    // The checkpoint stores the resolved project id (config._id), like the session store.
    const configId = useConfigStore.getState().config?._id
    const resolvedProjectId =
      typeof configId === 'string' && /^[a-f0-9]{24}$/i.test(configId) ? configId : projectId
    if (isOfferable(cp, { projectId: resolvedProjectId, activeSessionId })) setCheckpoint(cp)
  }, [projectId, enabled])

  useEffect(() => {
    if (!checkpoint) return
    if (offeredRef.current !== checkpoint.sessionId) {
      offeredRef.current = checkpoint.sessionId
      logOnPaidSession(checkpoint, 'Interrupted paid session offered to the guest.', {
        secondsSinceInterrupted: Math.round((Date.now() - checkpoint.savedAt) / 1000)
      })
    }
    // Nobody took it up inside the window: drop it and say so on the paid session.
    const timer = setTimeout(() => {
      logOnPaidSession(
        checkpoint,
        'Interrupted paid session not resumed: the offer expired. Refund the guest.',
        { level: 'warning' }
      )
      clearCheckpoint()
      setCheckpoint(null)
    }, msUntilExpiry(checkpoint))
    return (): void => clearTimeout(timer)
  }, [checkpoint])

  if (!enabled || !checkpoint) return null

  const tx = checkpoint.transaction as {
    amount?: number
    nativeAmount?: number
    currency?: string
    nativeCurrency?: string
  }
  const amountText = formatAmount(
    Number(tx.nativeAmount ?? tx.amount ?? 0),
    normalizeCurrency(tx.nativeCurrency ?? tx.currency)
  )
  const savedAtText = new Date(checkpoint.savedAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  })
  const cue = [amountText, savedAtText, describePackages(checkpoint.transaction)]
    .filter(Boolean)
    .join(' · ')

  const resume = (): void => {
    const { setSessionState, logActivity } = useSessionStateStore.getState()
    setSessionState({
      id: checkpoint.sessionId,
      projectId: checkpoint.projectId,
      ...(checkpoint.userId ? { userId: checkpoint.userId } : {}),
      ...(checkpoint.eventId ? { eventId: checkpoint.eventId } : {}),
      ...(checkpoint.mode ? { mode: checkpoint.mode as never } : {}),
      timeline: {
        startTime: checkpoint.startTime ? new Date(checkpoint.startTime) : new Date()
      },
      transaction: checkpoint.transaction as never,
      userPreferences: checkpoint.userPreferences as never,
      ...(checkpoint.galleryKey
        ? { galleryKey: checkpoint.galleryKey, gallery: { id: checkpoint.galleryKey } }
        : {})
    })
    useBoothEventStore.getState().setProjectContext(checkpoint.projectId)
    useBoothEventStore.getState().startBoothSession(checkpoint.sessionId)
    logActivity('Paid session resumed after the booth reloaded.', {
      checkpointSavedAt: new Date(checkpoint.savedAt).toISOString(),
      level: 'warning'
    })
    stateUploader.uploadSessionState(checkpoint.sessionId)

    // Same destination as view/Payment's navigateAfterSettlement, unless the flow named its own.
    const config = useConfigStore.getState().config
    const welcomeSettings = config?.pages?.welcome?.settings as
      | { customPath?: string; startWithPayment?: boolean }
      | undefined
    const applyAfterCapture = Boolean(config?.pages?.filter?.settings?.applyAfterCapture)
    const viewNextPage =
      welcomeSettings?.customPath === 'booth-daughter'
        ? 'capture'
        : welcomeSettings?.startWithPayment
          ? applyAfterCapture
            ? 'frame'
            : 'filter'
          : 'capture'
    const nextPage = flowNextPage ?? viewNextPage
    setCheckpoint(null)
    navigate(`/${projectId}/${nextPage}`)
  }

  const decline = (): void => {
    logOnPaidSession(checkpoint, 'Interrupted paid session declined: not the same guest.', {
      level: 'warning'
    })
    clearCheckpoint()
    setCheckpoint(null)
  }

  const title = t('interruptedSession.title', 'Your session was interrupted')
  const message = t('interruptedSession.message', "You've already paid. Carry on without paying again.")
  const resumeLabel = t('interruptedSession.resume', 'Continue my session')
  const declineLabel = t('interruptedSession.decline', 'Not my session')

  if (appearance === 'bahela') {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-8"
        // Welcome starts a session on a tap anywhere; a tap here must not.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative flex w-full max-w-3xl flex-col items-center rounded-3xl border-4 border-uriYellow bg-methadone/95 p-10 text-center shadow-2xl">
          <h2 className="mb-3 font-serif-bahela text-[48px] leading-tight text-uriYellow drop-shadow-2xl">
            {title}
          </h2>
          <p className="mb-6 font-body-bahela text-[28px] text-white drop-shadow-lg">{message}</p>
          <p
            className="mb-8 rounded-full bg-white/15 px-6 py-2 font-body-bahela text-[26px] text-white"
            data-testid="interrupted-session-cue"
          >
            {cue}
          </p>
          <BahelaButton onClick={resume} textColor="methadone" className="w-full !py-5 text-[34px]">
            {resumeLabel}
          </BahelaButton>
          <button
            type="button"
            onClick={decline}
            className="mt-6 font-body-bahela text-[22px] text-white/80 underline underline-offset-4"
          >
            {declineLabel}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-5 sm:p-8"
      // Welcome starts a session on a tap anywhere; a tap here must not.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex w-full max-w-xl flex-col items-center rounded-2xl bg-white p-8 text-center shadow-xl sm:p-10">
        <h2 className="mb-3 text-3xl font-bold">{title}</h2>
        <p className="mb-6 text-lg text-gray-600">{message}</p>
        <p
          className="mb-8 rounded-full bg-gray-100 px-5 py-2 text-lg font-semibold text-gray-800"
          data-testid="interrupted-session-cue"
        >
          {cue}
        </p>
        <Button text={resumeLabel} onClick={resume} className="!min-h-[64px] !w-full !px-8 !py-3 !text-xl" />
        <button
          type="button"
          onClick={decline}
          className="mt-5 text-base text-gray-500 underline underline-offset-4"
        >
          {declineLabel}
        </button>
      </div>
    </div>
  )
}

/** Write to the interrupted session's own log (not the empty store's) and send it now. */
function logOnPaidSession(
  checkpoint: SessionCheckpoint,
  message: string,
  details: Record<string, unknown>
): void {
  useSessionStateStore.getState().logActivity(
    message,
    {
      amount: (checkpoint.transaction as { amount?: number }).amount,
      checkpointSavedAt: new Date(checkpoint.savedAt).toISOString(),
      ...details
    },
    undefined,
    checkpoint.sessionId
  )
  void flushSessionLogs(checkpoint.sessionId)
}

export default InterruptedPaidSessionPrompt
